import { useState, useEffect, useCallback } from "react";
import { C, uid, fmt, Btn, Input, Select, Textarea, StatCard, BarChart, useIsMobile } from "./ui";
import { zonasProduccionService, zonasRacionService, zonasVentasService, zonasMovimientoService } from "../services/supabase";
import { toast } from "../services/toast";

export const TIPOS_ZONA = {
  Gallinero: { icon: "🐔", prodIcon: "🥚", animal: "gallinas", produce: "huevos",   unidad: "huevos",   vendeProd: true,  catVenta: "Lana / Productos" },
  Conejera:  { icon: "🐰", prodIcon: "🐇", animal: "conejos",  produce: "crías",    unidad: "crías",    vendeProd: true,  catVenta: "Venta de animales" },
  Chiquero:  { icon: "🐖", prodIcon: "🐷", animal: "chanchos", produce: "lechones", unidad: "lechones", vendeProd: true,  catVenta: "Venta de animales" },
};

const estacionUY = (d = new Date()) => {
  const m = d.getMonth() + 1;
  return (m === 12 || m <= 2) ? "verano" : m <= 5 ? "otoño" : m <= 8 ? "invierno" : "primavera";
};

const TIPS = {
  Gallinero: {
    verano:    "🌡️ Calor: agua fresca abundante y sombra. La postura puede bajar; sumá electrolitos y reduioná ración al mediodía.",
    otoño:     "🍂 Baja la luz natural y con ella la postura. Considerá luz artificial (14-16h) y subí calcio para cáscara firme.",
    invierno:  "❄️ Frío: más energía en la ración (maíz). Protegé del viento y cuidá la humedad de la cama para evitar enfermedades.",
    primavera: "🌸 Pico de postura. Asegurá calcio (conchilla) y proteína; es buena época para reponer ponedoras.",
  },
  Conejera: {
    verano:    "🌡️ Muy sensibles al calor: sombra, ventilación y agua fresca. Evitá montas en horas de calor extremo.",
    otoño:     "🍂 Buen momento reproductivo. Ajustá ración con más fibra (heno) y controlá parásitos.",
    invierno:  "❄️ Aumentá energía y protegé del frío/viento. Cuidá la humedad para evitar problemas respiratorios.",
    primavera: "🌸 Época ideal de reproducción. Buena alimentación a las madres lactantes; revisá nidos.",
  },
  Chiquero: {
    verano:    "🌡️ Necesitan barro/agua para refrescarse (no transpiran). Sombra obligatoria y agua a voluntad.",
    otoño:     "🍂 Buen engorde. Ración balanceada constante; aprovechá restos de cosecha si tenés.",
    invierno:  "❄️ Más energía en la dieta y cama seca abundante. Protegé del frío, sobre todo a los lechones.",
    primavera: "🌸 Época de partos frecuente. Cuidá la higiene del parto y la lactancia de las madres.",
  },
};

export function ZonasPanel({ zonasState, insumosState, user, profile, onIngreso, onConsumirRacion }) {
  const isMobile = useIsMobile();
  const { zonas, createZona, deleteZona, updateZona } = zonasState;
  const { insumos = [] } = insumosState || {};
  const [show, setShow] = useState(false);
  const [tipo, setTipo] = useState("Gallinero");
  const [nombre, setNombre] = useState("");
  const [animales, setAnimales] = useState("");
  const [accion, setAccion] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const [verHist, setVerHist] = useState(null);

  // Datos normalizados
  const [producciones, setProducciones] = useState({});
  const [raciones, setRaciones] = useState({});
  const [ventas, setVentas] = useState({});
  const [movimientos, setMovimientos] = useState({});

  const estacion = estacionUY();
  const usuarioId = user?.id;
  const campoId = profile?.campo_propietario_id || user?.id;

  // Cargar datos normalizados al montar
  const cargarDatos = useCallback(async (zonaId) => {
    if (!zonaId || !usuarioId || !campoId) return;
    try {
      const [prod, rac, vent, mov] = await Promise.all([
        zonasProduccionService.getByZona(zonaId),
        zonasRacionService.getByZona(zonaId),
        zonasVentasService.getByZona(zonaId),
        zonasMovimientoService.getByZona(zonaId)
      ]);
      setProducciones(prev => ({ ...prev, [zonaId]: prod || [] }));
      setRaciones(prev => ({ ...prev, [zonaId]: rac || [] }));
      setVentas(prev => ({ ...prev, [zonaId]: vent || [] }));
      setMovimientos(prev => ({ ...prev, [zonaId]: mov || [] }));
    } catch (e) {
      console.error("Error cargando datos de zona:", e);
    }
  }, [usuarioId, campoId]);

  // Cargar datos de todas las zonas
  useEffect(() => {
    zonas.forEach(z => cargarDatos(z.id));
  }, [zonas, cargarDatos]);

  const crear = async () => {
    if (!nombre) return;
    try {
      await createZona({
        tipo,
        nombre,
        animales: parseInt(animales) || 0,
      });
      setShow(false);
      setNombre("");
      setAnimales("");
      toast.success("Zona creada.");
    } catch (e) {
      console.error("Error creando zona:", e);
      toast.error("No se pudo crear la zona: " + (e.message || "error"));
    }
  };

  const upd = async (id, patch) => {
    const zonaAct = zonas.find(z => z.id === id);
    if (zonaAct) {
      await updateZona(id, { ...zonaAct, ...patch });
    }
  };

  const eliminar = async (id) => {
    try {
      await deleteZona(id);
      setConfirmar(null);
    } catch (e) {
      console.error("Error eliminando zona:", e);
    }
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", maxWidth: 880, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>
          Zonas productivas
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>
          Gallinero, conejera y chiquero. Todos los datos guardados en base de datos.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <Btn variant="primary" onClick={() => setShow(v => !v)}>{show ? "Cancelar" : "+ Nueva zona"}</Btn>
        </div>

        {show && (
          <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${C.arenaDark}` }}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: "0 12px" }}>
              <Select label="Tipo" value={tipo} onChange={setTipo} options={Object.keys(TIPOS_ZONA)} />
              <Input label="Nombre" value={nombre} onChange={setNombre} placeholder="Ej: Gallinero del fondo" />
              <Input label={`Nº de ${TIPOS_ZONA[tipo].animal}`} value={animales} onChange={setAnimales} type="number" placeholder="0" />
            </div>
            <Btn variant="primary" onClick={crear} disabled={!nombre} style={{ width: "100%" }}>Crear zona</Btn>
          </div>
        )}

        {zonas.length === 0 && !show && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🐔</div>
            <div style={{ fontSize: 13 }}>Creá un gallinero, conejera o chiquero para llevar su producción.</div>
          </div>
        )}

        {zonas.map((z) => {
          const cfg = TIPOS_ZONA[z.tipo] || TIPOS_ZONA.Gallinero;
          const prod = producciones[z.id] || [];
          const rac = raciones[z.id] || [];
          const vent = ventas[z.id] || [];
          const movs = movimientos[z.id] || [];
          const totalProd = prod.reduce((s, p) => s + (p.cantidad || 0), 0);
          const totalRacion = rac.reduce((s, r) => s + (r.costo || 0), 0);
          const totalVentas = vent.reduce((s, v) => s + (v.monto || 0), 0);
          const abierto = accion?.zonaId === z.id;

          return (
            <div key={z.id} style={{ background: C.white, borderRadius: 12, padding: "14px 16px", marginBottom: 10, border: `1px solid ${C.arenaDark}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 30 }}>{cfg.icon}</span>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.tierra }}>{z.nombre}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {z.tipo} · <b>{z.animales}</b> {cfg.animal} · {totalProd} {cfg.unidad} acumulados
                  </div>
                </div>
                {confirmar === z.id ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => { eliminar(z.id); setConfirmar(null); }} style={{ ...btnZ, background: C.danger, color: "#fff", borderColor: C.danger }}>Sí, borrar</button>
                    <button onClick={() => setConfirmar(null)} style={btnZ}>No</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmar(z.id)} style={{ ...btnZ, color: C.danger, borderColor: "#E0909055" }}>✕</button>
                )}
              </div>

              {/* Mini-stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 8, margin: "12px 0" }}>
                <StatCard val={z.animales} label={cfg.animal} />
                <StatCard val={totalProd} label={cfg.unidad} color={C.pasto} />
                <StatCard val={`${totalRacion} kg`} label="ración usada" />
                <StatCard val={`$${totalVentas.toLocaleString("es-UY")}`} label="ventas" color={C.tierra} />
              </div>

              {/* Recomendación dinámica según la época */}
              <div style={{ background:"#FBF6EA", border:"1px solid #E8D9B0", borderRadius:9,
                padding:"9px 12px", margin:"0 0 12px", fontSize:12, color:"#6B5618", lineHeight:1.5 }}>
                <b style={{ textTransform:"capitalize" }}>💡 {estacion}:</b> {(TIPS[z.tipo]||{})[estacion]}
              </div>

              {/* Acciones */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "prod" ? null : { zonaId: z.id, modo: "prod" })}>
                  {cfg.prodIcon} Producción
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "racion" ? null : { zonaId: z.id, modo: "racion" })}>
                  🌾 Ración
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "venta" ? null : { zonaId: z.id, modo: "venta" })}>
                  💰 Venta
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => setAccion(abierto && accion.modo === "ingreso" ? null : { zonaId: z.id, modo: "ingreso" })}>
                  + Ingreso {cfg.animal}
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => setVerHist(verHist === z.id ? null : z.id)}>
                  {verHist === z.id ? "▾ historial" : "▸ historial"}
                </Btn>
              </div>

              {abierto && (
                <AccionForm zona={z} cfg={cfg} modo={accion.modo} insumos={insumos} usuarioId={usuarioId} campoId={campoId}
                  producciones={prod} raciones={rac} ventas={vent}
                  onClose={() => setAccion(null)}
                  onProdAdd={async (p) => {
                    await zonasProduccionService.create(usuarioId, campoId, z.id, p);
                    await cargarDatos(z.id);
                  }}
                  onRacionAdd={async (r) => {
                    await zonasRacionService.create(usuarioId, campoId, z.id, r);
                    await cargarDatos(z.id);
                    if (onConsumirRacion) onConsumirRacion(r.insumo_id, r.kg, z.nombre, r.fecha);
                  }}
                  onVentaAdd={async (v, nuevoAnimales) => {
                    // Solo registrar venta si es una venta real (el "ingreso de
                    // animales" manda {} y únicamente actualiza el conteo).
                    if (v && v.cantidad != null) {
                      await zonasVentasService.create(usuarioId, campoId, z.id, v);
                    }
                    if (nuevoAnimales !== undefined) {
                      await upd(z.id, { animales: nuevoAnimales });
                    }
                    await cargarDatos(z.id);
                  }}
                  onMovimientoAdd={async (m) => {
                    await zonasMovimientoService.create(usuarioId, campoId, z.id, m);
                    await cargarDatos(z.id);
                  }}
                  onIngreso={onIngreso}
                />
              )}

              {verHist === z.id && (
                <>
                  <EconomiaZona prod={prod} rac={rac} vent={vent} cfg={cfg} />
                  <Historial prod={prod} rac={rac} vent={vent} movs={movs} cfg={cfg} />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AccionForm({ zona, cfg, modo, insumos, usuarioId, campoId, producciones, raciones, ventas, onClose, onProdAdd, onRacionAdd, onVentaAdd, onMovimientoAdd, onIngreso }) {
  const hoy = () => new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy());
  const [cant, setCant] = useState("");
  const [monto, setMonto] = useState("");
  const [queVende, setQueVende] = useState(cfg.vendeProd ? "produccion" : "animales");

  const racionesStock = insumos.filter(i => i.categoria === "Sal/Ración");
  const [racionId, setRacionId] = useState(racionesStock[0]?.id || "");
  const [costo, setCosto] = useState("");
  const racionSel = racionesStock.find(i => i.id === racionId);
  const racionUnit = racionSel?.costo_unitario || 0;
  const excedeRacion = modo === "racion" && racionSel && (parseFloat(cant) || 0) > (racionSel.cantidad || 0);

  const totalProdAcum = producciones.reduce((s,p)=>s+(p.cantidad||0),0);
  const totalProdVendida = ventas.filter(v=>v.que===cfg.produce).reduce((s,v)=>s+(v.cantidad||0),0);
  const stockProdDisponible = totalProdAcum - totalProdVendida;
  const excedeStock = modo === "venta" && (
    queVende === "animales" ? (parseFloat(cant) || 0) > (zona.animales || 0)
                            : (parseFloat(cant) || 0) > stockProdDisponible
  );

  const guardar = async () => {
    const c = parseFloat(cant) || 0;
    try {
      if (modo === "prod") {
        if (c <= 0) return;
        await onProdAdd({ fecha, cantidad: c });
        toast.success("Producción registrada.");
      } else if (modo === "racion") {
        if (c <= 0 || excedeRacion) return;
        const costoRacion = Math.round(c * racionUnit);
        await onRacionAdd({ fecha, kg: c, costo: costoRacion, nombre: racionSel?.nombre, insumo_id: racionId });
        toast.success("Ración registrada.");
      } else if (modo === "venta") {
        const m = parseFloat(monto) || 0;
        if (c <= 0 || m <= 0 || excedeStock) return;
        const detalle = queVende === "produccion" ? cfg.produce : cfg.animal;
        await onVentaAdd({ fecha, que: detalle, cantidad: c, monto: m }, queVende === "animales" ? Math.max(0, (zona.animales || 0) - c) : undefined);
        if (onIngreso) onIngreso({
          tipo: "ingreso", categoria: cfg.catVenta,
          concepto: `Venta ${detalle} (${c}) · ${zona.nombre}`, monto: m, fecha, obs: "Generado desde Zonas", zona_id: zona.id,
        });
        toast.success("Venta registrada.");
      } else if (modo === "ingreso") {
        if (c <= 0) return;
        await onVentaAdd({}, (zona.animales || 0) + c);
        if (onMovimientoAdd) await onMovimientoAdd({ tipo: "ingreso", fecha, cantidad: c, costo: parseFloat(costo) || null });
        if (costo && onIngreso) onIngreso({
          tipo: "egreso", categoria: "Compra de animales",
          concepto: `Ingreso ${c} ${cfg.animal} · ${zona.nombre}`, monto: parseFloat(costo) || 0, fecha, obs: "Generado desde Zonas",
        });
        toast.success(`+${c} ${cfg.animal} en la zona.`);
      }
      onClose();
    } catch (e) {
      toast.error("No se pudo guardar: " + (e.message || "error"));
    }
  };

  return (
    <div style={{ background: C.fondo, borderRadius: 10, padding: 14, marginTop: 12, border: `1px solid ${C.arenaDark}` }}>
      {modo === "prod" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
          <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
          <Input label={`Cantidad de ${cfg.unidad}`} value={cant} onChange={setCant} type="number" placeholder="Ej: 24" />
        </div>
      )}
      {modo === "racion" && (
        <>
          {racionesStock.length > 0 ? (
            <Select label="¿Qué ración usás? (sale del stock)" value={racionId} onChange={setRacionId}
              options={racionesStock.map(i => ({ v: i.id, l: `${i.nombre} — ${i.cantidad} ${i.unidad} en stock` }))} />
          ) : (
            <div style={{ fontSize: 11, color: C.danger, marginBottom: 10, lineHeight: 1.5 }}>
              ⚠ No tenés ración en el stock. Andá a <b>Insumos → + Agregar insumo</b>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
            <Input label="Ración usada (kg)" value={cant} onChange={setCant} type="number" placeholder="Ej: 10" />
          </div>
          {racionSel && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              Disponible: <b>{racionSel.cantidad} {racionSel.unidad}</b> · ${(racionUnit).toLocaleString("es-UY", { maximumFractionDigits: 2 })}/kg
            </div>
          )}
          {excedeRacion && (
            <div style={{ fontSize: 11.5, color: C.danger, fontWeight: 600, marginBottom: 8 }}>
              ⚠ No podés usar más de lo que tenés.
            </div>
          )}
        </>
      )}
      {modo === "venta" && (
        <>
          <Select label="¿Qué vendés?" value={queVende} onChange={setQueVende}
            options={[{ v: "produccion", l: `Producción (${cfg.produce})` }, { v: "animales", l: cfg.animal }]} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 12px" }}>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
            <Input label="Cantidad" value={cant} onChange={setCant} type="number" placeholder="0" />
            <Input label="Monto total ($)" value={monto} onChange={setMonto} type="number" placeholder="0" />
          </div>
          {excedeStock && (
            <div style={{ fontSize: 11.5, color: C.danger, fontWeight: 600, marginBottom: 8, marginTop: 8 }}>
              ⚠ No podés vender más de lo que tenés.
            </div>
          )}
        </>
      )}
      {modo === "ingreso" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
          <Input label="Fecha" value={fecha} onChange={setFecha} type="date" />
          <Input label={`Cantidad de ${cfg.animal}`} value={cant} onChange={setCant} type="number" placeholder="0" />
          <Input label="Costo total ($, opcional)" value={costo} onChange={setCosto} type="number" placeholder="0" />
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn size="sm" variant="ghost" onClick={onClose} style={{ flex: 1 }}>Cancelar</Btn>
        <Btn size="sm" variant="primary" onClick={guardar} disabled={excedeRacion || excedeStock} style={{ flex: 1 }}>Guardar</Btn>
      </div>
    </div>
  );
}

function Historial({ prod, rac, vent, movs = [], cfg }) {
  const eventos = [
    ...prod.map(p => ({ fecha: p.fecha, ic: cfg.prodIcon, txt: `Producción`, val: `+${p.cantidad} ${cfg.unidad}`, col: C.pasto })),
    ...rac.map(r => ({ fecha: r.fecha, ic: "🌾", txt: `Ración (${r.nombre})`, val: `−${r.kg} kg${r.costo ? ` · $${r.costo.toLocaleString("es-UY")}` : ""}`, col: C.danger })),
    ...vent.map(v => ({ fecha: v.fecha, ic: "💰", txt: `Venta ${v.que} (${v.cantidad})`, val: `+$${(v.monto || 0).toLocaleString("es-UY")}`, col: C.tierra })),
    ...movs.map(m => ({ fecha: m.fecha, ic: cfg.icon || "🐣", txt: `Ingreso ${cfg.animal}${m.costo ? ` · $${(m.costo).toLocaleString("es-UY")}` : ""}`, val: `+${m.cantidad}`, col: C.pasto })),
  ].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${C.arena}`, paddingTop: 10 }}>
      {eventos.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Sin movimientos todavía.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
        {eventos.map((e, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12,
            background: C.fondo, borderRadius: 6, padding: "6px 10px" }}>
            <span style={{ fontSize: 15 }}>{e.ic}</span>
            <span style={{ color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>{fmt(e.fecha)}</span>
            <span style={{ flex: 1 }}>{e.txt}</span>
            <span style={{ fontWeight: 700, color: e.col, whiteSpace: "nowrap" }}>{e.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EconomiaZona({ prod, rac, vent, cfg }) {
  const totalProdAcum = prod.reduce((s, p) => s + (p.cantidad || 0), 0);
  const totalRacion = rac.reduce((s, r) => s + (r.costo || 0), 0);
  const totalVentas = vent.reduce((s, v) => s + (v.monto || 0), 0);
  const margen = totalVentas - totalRacion;
  const margenPct = totalVentas > 0 ? Math.round((margen / totalVentas) * 100) : 0;

  const chartData = [
    { label: "Ración", value: totalRacion, color: C.danger },
    { label: "Ventas", value: totalVentas, color: C.pasto },
  ];

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${C.arena}`, paddingTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.tierra, marginBottom: 10 }}>📊 Economía de {cfg.animal}</div>
      {totalVentas === 0 ? (
        <div style={{ fontSize: 12, color: C.muted }}>Sin ventas registradas.</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            <StatCard val={`$${totalRacion.toLocaleString("es-UY")}`} label="Ración" color={C.danger} />
            <StatCard val={`$${totalVentas.toLocaleString("es-UY")}`} label="Ventas" color={C.pasto} />
            <StatCard val={`$${margen.toLocaleString("es-UY")}`} label={`Margen ${margenPct > 0 ? "✓" : "↓"}`} color={margen > 0 ? C.pasto : C.danger} />
          </div>
          {chartData.some(d => d.value > 0) && <BarChart data={chartData} />}
        </>
      )}
    </div>
  );
}

const btnZ = {
  background: C.arena, border: `1px solid ${C.arenaDark}`, borderRadius: 6,
  padding: "6px 9px", fontSize: 12, cursor: "pointer", color: C.tierra, fontFamily: "monospace", flexShrink: 0,
};
